import { useState, useRef, useMemo, useEffect, ReactNode, ChangeEvent, DragEvent, KeyboardEvent } from 'react';
import { InputGroup, InputGroupText, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'src/components/ui';
import useUID from '../util/useUID';
import classnames from '../util/classnames';
import { Trans } from '@lingui/react/macro';
import Icon from 'src/components/Icon';

const ICONS: Record<string, string> = {
  pdf: 'fa fa-file-pdf',
  doc: 'fa fa-file-word',
  txt: 'FileText',
  png: 'FileImage',
  jpg: 'FileImage',
  jpeg: 'FileImage',
  zip: 'FileArchive',
  default: 'File',
};

type FileValue = {
  name: string;
  url?: string;
} | null;

type FileInputProps = {
  file: FileValue;
  disabled?: boolean;
  viewable?: boolean;
  viewButtonText?: ReactNode;
  uploadButtonText?: ReactNode;
  onFileSelected: (file: File | null) => void;
  inlinePreview?: boolean;
  resource?: string;
  accept?: string;
  readonly?: boolean;
  required?: boolean;
  inputClassName?: string;
  placeholder?: ReactNode;
  uploadingFile?: boolean;
  loadingFile?: boolean;
  fieldTitle?: string;
  hasError?: boolean;
  clearable?: boolean;
  'data-testid'?: string;
};

function FileInput({
  file,
  disabled,
  viewable = true,
  viewButtonText,
  uploadButtonText,
  onFileSelected,
  inlinePreview,
  resource = 'File',
  accept = '*',
  readonly = false,
  required = false,
  inputClassName = '',
  placeholder = '',
  uploadingFile = false,
  loadingFile = false,
  fieldTitle = '',
  hasError = false,
  clearable = false,
  'data-testid': dataTestid,
}: FileInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const fieldId = useUID('fileinput-');

  const [clearCt, setClearCt] = useState(0);
  const [dragTarget, setDragTarget] = useState(false);
  const [modal, setModal] = useState(false);

  async function onChange({ target: { files } }: ChangeEvent<HTMLInputElement>) {
    if (disabled) return;
    const selectedFile = files?.[0];
    if (selectedFile) {
      await onFileSelected(selectedFile);
      setClearCt(clearCt + 1);
    }
  }

  function onKeyPress(e: KeyboardEvent) {
    if (disabled) return;
    if (e.which === 32 || e.which === 13) {
      e.stopPropagation();
      e.preventDefault();
      if (ref.current) ref.current.click();
    }
  }

  function onDrop(event: DragEvent) {
    if (disabled) return;
    setDragTarget(false);
    event.preventDefault();
    const { dataTransfer: { files: dtFiles, items: dtItems } } = event;
    const dropped = (dtItems
      ? Array.from(dtItems).filter(i => i.kind === 'file').map(i => i.getAsFile()!)
      : Array.from(dtFiles))[0];
    if (dropped) onChange({ target: { files: [dropped] } } as any);
  }

  function onDragOver(event: DragEvent) {
    if (disabled) return;
    event.preventDefault();
    const { dataTransfer: { files: dtFiles, items: dtItems } } = event;
    const hasFile = dtItems ? Array.from(dtItems).some(i => i.kind === 'file') : dtFiles.length > 0;
    setDragTarget(hasFile);
  }

  function onDragLeave() {
    if (disabled) return;
    setDragTarget(false);
  }

  const extension = useMemo(() => ((file && file.name) || '').split('.').pop() ?? '', [file]);

  function modalToggle(open = false) {
    setModal(open);
  }

  const placeholderText: ReactNode = useMemo(() => {
    if (uploadingFile) return <Trans>Uploading file...</Trans>;
    if (loadingFile) return <Trans>Loading file...</Trans>;
    if (placeholder) return placeholder;
    if (readonly) return <Trans>No file uploaded</Trans>;
    return <Trans>Upload or drop a file</Trans>;
  }, [uploadingFile, loadingFile, placeholder]);

  const inputGroup = (
    <InputGroup
      className={classnames('file-input', { 'border-success': dragTarget, 'border-danger': hasError })}
      onDrop={!readonly ? onDrop : undefined}
      onDragOver={!readonly ? onDragOver : undefined}
      onDragLeave={!readonly ? onDragLeave : undefined}
      size="lg"
    >
      <span className="file-input-info">
        <InputGroupText>
          {uploadingFile || loadingFile ? <Spinner /> : <Icon icon={file ? (ICONS[extension] || ICONS.default) : ICONS.default} />}
        </InputGroupText>
        <span className="file-input-info-text">
          {fieldTitle && fieldTitle !== ' ' && (
            <label className="control-label file-input-title">
              {fieldTitle}
              {required && <span className="required">*</span>}
            </label>
          )}
          {file && file.name ? (
            <span className="file-input-name">{file.name}</span>
          ) : (
            <small className="file-input-placeholder">
              <em>{placeholderText}</em>
            </small>
          )}
        </span>
      </span>
      <span className="file-input-toolbar">
        {viewable && file && file.url ? (
          <>
            <Button color="info" onClick={() => modalToggle(true)}>
              {viewButtonText || <Icon icon="Eye" />}
            </Button>
            <FilePreviewModal isOpen={modal} toggle={() => modalToggle()} name={file.name} url={file.url} extension={extension} />
          </>
        ) : null}
        {!readonly && clearable && file ? (
          <Button
            size="sm"
            color="danger"
            style={{ height: 'fit-content', background: 'transparent', border: 'none' }}
            onClick={() => {
              onFileSelected(null);
              setClearCt(c => c + 1);
            }}
            title="Remove file"
          >
            <Icon icon="X" weight="regular" />
          </Button>
        ) : null}
        {!readonly ? (
          dragTarget ? (
            <Button size="sm" color="success" className="file-input-label">
              <Trans>Drop File</Trans>
            </Button>
          ) : (
            <Button
              style={{ height: 'fit-content' }}
              size="sm"
              color="primary"
              disabled={disabled}
              className="file-input-label"
              tag="label"
              htmlFor={fieldId}
              tabIndex={0}
              onKeyPress={onKeyPress}
            >
              <input
                key={clearCt}
                id={fieldId}
                data-testid={dataTestid}
                className={inputClassName}
                ref={ref}
                type="file"
                accept={accept}
                required={required && !(file && file.url)}
                onChange={onChange}
              />
              <Icon icon="Upload" /> {uploadButtonText || <Trans>Upload</Trans>}
            </Button>
          )
        ) : null}
      </span>
      {required && <Icon icon="Star" className="file-input-required-icon fa" />}
    </InputGroup>
  );

  return inlinePreview ? (
    <div className="file-input inline-preview">
      {file?.url ? <FilePreview url={file.url} extension={extension} /> : null}
      {inputGroup}
    </div>
  ) : (
    inputGroup
  );
}

type FilePreviewModalProps = {
  isOpen: boolean;
  toggle: () => void;
  name?: string;
  url: string;
  extension: string;
};

function FilePreviewModal({ isOpen, toggle, url, extension }: FilePreviewModalProps) {
  return (
    <Modal isOpen={isOpen} toggle={toggle} className="modal-primary" size="xl" backdrop>
      <ModalHeader toggle={toggle}>
        <Trans>File Preview</Trans>
      </ModalHeader>
      <ModalBody style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FilePreview url={url} extension={extension} />
      </ModalBody>
      <ModalFooter>
        <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer">
          <Trans>Download</Trans>
        </a>
        <Button onClick={toggle} color="secondary">
          <Trans>Close</Trans>
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type FilePreviewProps = {
  url: string;
  extension: string;
};

function FilePreview({ url, extension }: FilePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [iframeContent, setIframeContent] = useState<string | null>(null);
  const isImage = useMemo(() => /png|jpg|jpeg/i.test(extension), [extension]);

  useEffect(() => {
    if (!isImage) {
      const fetchDocument = async () => {
        try {
          const response = await fetch(url);
          const content = await response.blob();
          const contentType = extension === 'pdf' ? 'application/pdf' : content.type;
          const newBlob = new Blob([content], { type: contentType });
          if (!response.ok) throw new Error('No se pudo cargar el documento');
          setIframeContent(URL.createObjectURL(newBlob));
          setIsLoading(false);
        } catch {
          setLoadError(true);
          setIsLoading(false);
        }
      };
      fetchDocument();
      return () => {
        if (iframeContent) URL.revokeObjectURL(iframeContent);
      };
    } else {
      setIsLoading(false);
    }
  }, [url]);

  return (
    <div className="modal-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {isLoading ? (
        <p><Trans>Loading...</Trans></p>
      ) : loadError ? (
        <p><Trans>Error loading file</Trans></p>
      ) : isImage ? (
        <img src={url} alt="File" width="75%" style={{ alignSelf: 'center' }} />
      ) : (
        <iframe title="Files" src={iframeContent ?? undefined} height="500" width="100%" />
      )}
    </div>
  );
}

export default FileInput;
